import { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader, TONE_STYLE, UserNameCell } from '@/components/core';
import { Badge } from '@/components/ui-kit/badge';
import { cn } from '@/lib/utils';
import { REVIEW_TYPES } from '@/modules/shared/hr-form-engine';
import {
  formatDate,
  getApplicableReviewTypes,
  PmReview,
} from '@/modules/performance/hr-review-core';
import { REVIEW_STATUS_TONE, reviewStatusKey } from '../../constants/review-status';

interface CreateReviewsTableColumnsProps {
  t: (key: string) => string;
  departmentNames: Map<string, string>;
  designationNames: Map<string, string>;
  /** The employee's email by IAM user id, shown under their name. */
  emailOf?: (userId: string) => string | undefined;
}

export const createReviewsTableColumns = ({
  t,
  departmentNames,
  designationNames,
  emailOf,
}: CreateReviewsTableColumnsProps): ColumnDef<PmReview>[] => [
  {
    id: 'Name',
    // Filtered by the toolbar's name search.
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMPLOYEE_NAME')} />,
    accessorFn: (row) => row.Name,
    cell: ({ row }) => (
      <UserNameCell name={row.original.Name} email={emailOf?.(row.original.ReviewForId)} />
    ),
  },
  {
    id: 'Department',
    // Looked up from the department id; filtered by the toolbar.
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DEPARTMENT')} />,
    accessorFn: (row) => departmentNames.get(row.DepartmentId ?? '') ?? '-',
  },
  {
    id: 'Designation',
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DESIGNATION')} />,
    accessorFn: (row) => designationNames.get(row.DesignationId ?? '') ?? '-',
  },
  {
    id: 'CreatedDate',
    meta: { filter: { type: 'datetime' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('REQUEST_DATE')} />,
    accessorFn: (row) => formatDate(row.CreatedDate),
  },
  {
    id: 'ReviewTypes',
    header: () => <span className="text-xs font-medium">{t('REVIEW_TYPES')}</span>,
    enableSorting: false,
    cell: ({ row }) => {
      const applicable = getApplicableReviewTypes(row.original);
      if (applicable.length === 0) return <span>-</span>;
      return (
        <div className="flex flex-wrap gap-1">
          {applicable.map(({ reviewType, status, deadline }) => (
            <Badge
              key={reviewType}
              variant="outline"
              className={cn(
                'whitespace-normal rounded-lg text-left font-medium',
                TONE_STYLE[REVIEW_STATUS_TONE[status]]
              )}
            >
              {t(REVIEW_TYPES.find((type) => type.key === reviewType)?.title ?? reviewType)}:{' '}
              {t(reviewStatusKey(status))}
              {deadline ? ` (${formatDate(deadline)})` : ''}
            </Badge>
          ))}
        </div>
      );
    },
  },
];
